import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import styles from "./OTP.module.css";
import OtpInput from "react-otp-input";
import validator from "validator";
import { otpVerifyAction } from "@/services/PreEnrollmentService";
import { trackEventPreEnrolment } from "@/services/mixpanel";

interface OTPScreenProps {
  handleChangePhoneNumber: () => void;
  mobileNumber: string;
  mobileWithoutCode: string | undefined;
  countryCode: string | undefined;
  resendOTP: () => void;
  handleLeadsData: (is_otp_verified?: 0 | 1) => void;
  setStep: Dispatch<SetStateAction<number>>;
  handleExistingUser: () => void;
}

const OTPScreen = ({
  handleChangePhoneNumber,
  mobileWithoutCode,
  countryCode,
  resendOTP,
  handleLeadsData,
  // setStep,
  handleExistingUser,
  mobileNumber,
}: OTPScreenProps) => {
  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(30);
  const [showResendButton, setShowResendButton] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (timeLeft === 0) {
      setShowResendButton(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleResendOTP = () => {
    setClickCount((prevCount) => prevCount + 1);

    setTimeLeft(30);
    setShowResendButton(false);
    setError("");

    resendOTP();

    trackEventPreEnrolment("otp_resend_clicked", {
      resend_attempt_count: clickCount + 1,
    });
  };

  const handleOtpChange = (value: any) => {
    setOtp(validator.isNumeric(value) ? value : "");
    setError("");
  };

  const handleKeyPress = (e: any) => {
    if (!validator.isNumeric(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: any) => {
    const pastedValue = e.clipboardData.getData("Text");
    const numericValue = validator.isNumeric(pastedValue) ? pastedValue : "";
    setOtp(numericValue);
    e.preventDefault();
  };

  const handleOtpSubmit = async () => {
    if (!otp || otp.length !== 4 || !validator.isNumeric(otp)) {
      setError("Invalid OTP. Please enter a valid OTP");
      setOtp("");
      return;
    }
    const payload = {
      mobileNumber: mobileWithoutCode,
      mobileCountryCode: countryCode,
      otp: otp,
    };

    if (payload) {
      try {
        const response = await otpVerifyAction(payload);
        if (response?.statusCode === 201) {
          const accressToken = response?.data?.tokenDetails?.access_token;
          localStorage.setItem("access_token", accressToken);
          if (mobileNumber)
            trackEventPreEnrolment("otp_verify_clicked", {
              phone_number: mobileNumber,
            });

          handleLeadsData(1);
          await handleExistingUser();
        }
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("Wrong OTP. Please try again.");
        }
        setOtp("");
        console.error(error);
      }
    }
  };

  return (
    <>
      <div className={styles.otpCompoContainer}>
        <h2 className={styles.otpHeader}>OTP Verification</h2>
        <p className={styles.otpSubText}>
          Enter the 4-digit OTP sent to your number +{countryCode}{" "}
          {mobileWithoutCode}
        </p>
      </div>
      <div>
        <OtpInput
          value={otp}
          onChange={handleOtpChange}
          numInputs={4}
          shouldAutoFocus
          renderInput={(props: any) => (
            <input
              {...props}
              className={styles.otpInput}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
            />
          )}
          containerStyle={styles.otpContainer}
        />
        {error && <p className={styles.otpErrorMessage}>{error}</p>}
        <div>
          <p className={styles.optSentText}>
            We&#39;ve sent a code to +{countryCode} {mobileWithoutCode}
          </p>
          {showResendButton ? (
            <span onClick={handleResendOTP} className={styles.resendPhone}>
              Resend Code
            </span>
          ) : (
            <p className={styles.otpTime}>Resend OTP in {timeLeft} sec</p>
          )}
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-center mt-5">
        <button className={styles.otpVerifyButton} onClick={handleOtpSubmit}>
          Verify and Proceed
        </button>
      </div>
      <span className={styles.changePhone} onClick={handleChangePhoneNumber}>
        Change Phone Number
      </span>
    </>
  );
};

export default OTPScreen;
